import { GUID } from "@faro-lotv/foundation";
import { ISOTimeString } from "@faro-lotv/ielement-types";
import { clearStore } from "@faro-lotv/project-source";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DateTime } from "luxon";

/**
 * The ProjectFilteringSlice is used to manage the state of project filtering in the application.
 * It allows users to filter the project based on selected tags and date ranges.
 */
export type ProjectFilteringSlice = {
  /** The selected text for project filtering, case insensitive. */
  text: string;

  /** The selected tags for project filtering. */
  tags?: GUID[];

  /** The selected date range for project filtering. */
  dateRange: {
    /** The start date of the selected date range. */
    start?: ISOTimeString;
    /** The end date of the selected date range. */
    end?: ISOTimeString;
  };
};

export type OnlyProjectFilteringStore = {
  projectFiltering: ProjectFilteringSlice;
};

export const initialState: ProjectFilteringSlice = {
  text: "",
  dateRange: {},
};

const projectFilteringSlice = createSlice({
  name: "projectFiltering",
  initialState,
  reducers: {
    /**
     * Set which tags the user selected for project filtering.
     *
     * @param state The current state
     * @param action The list of tags to set as selected. If empty or undefined, it clears the selection.
     */
    setSelectedTags(state, action: PayloadAction<GUID[] | undefined>) {
      const selectedTags = action.payload;
      if (selectedTags?.length) {
        state.tags = selectedTags;
      } else {
        state.tags = undefined;
      }
    },

    /**
     * Set the start date for project filtering, moving the provided date to the beginning of day.
     *
     * @param state The current state
     * @param action The start date to set for project filtering. If undefined, it clears the start date.
     */
    setSelectedStartDate(
      state,
      action: PayloadAction<ISOTimeString | undefined>,
    ) {
      const selectedStartDate = action.payload ?? "";

      if (selectedStartDate === "") {
        state.dateRange.start = undefined;
        return;
      }

      state.dateRange.start =
        DateTime.fromISO(selectedStartDate).startOf("day").toISO() ?? undefined;
    },

    /**
     * Set the end date for project filtering, moving the provided date to the end of day.
     *
     * @param state The current state
     * @param action The end date to set for project filtering. If undefined, it clears the end date.
     */
    setSelectedEndDate(
      state,
      action: PayloadAction<ISOTimeString | undefined>,
    ) {
      const selectedEndDate = action.payload ?? "";

      if (selectedEndDate === "") {
        state.dateRange.end = undefined;
        return;
      }

      state.dateRange.end =
        DateTime.fromISO(selectedEndDate).endOf("day").toISO() ?? undefined;
    },

    /**
     * Set the search text for project filtering.
     *
     * @param state The current state
     * @param action The search text to set for project filtering. If undefined or empty, it clears the search text.
     */
    setSearchText(state, action: PayloadAction<string | undefined>) {
      state.text = action.payload?.trim() ?? "";
    },

    /**
     * Reset selected tags and dates while preserving search text.
     *
     * @param state The current state
     */
    resetTagsAndDates(state) {
      state.tags = undefined;
      state.dateRange = {};
    },

    /**
     * Replace the entire filtering state, e.g. when restoring a saved view.
     *
     * The date setters above snap to the start/end of the day, so a stored range
     * has to be applied as-is instead of through them.
     *
     * @param _state The current state
     * @param action The full filtering state to apply
     * @returns the new state
     */
    setProjectFiltering(_state, action: PayloadAction<ProjectFilteringSlice>) {
      return action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const projectFilteringReducer = projectFilteringSlice.reducer;

export const {
  setSelectedTags,
  setSelectedStartDate,
  setSelectedEndDate,
  setSearchText,
  resetTagsAndDates,
  setProjectFiltering,
} = projectFilteringSlice.actions;
